import React, { useEffect, useState, useRef, RefObject } from 'react';
import Button from '.';
import './OptionsButton.scss';
import Icon from '../../components/_base/Icon';
import { MediaQueryContextType, useMediaQuery } from '../../lib/context-providers/media-query-provider';
import { CALENDAR_INVITES } from '../../lib/constants';

const calendarInviteTopOffsets: Record<CALENDAR_INVITES, string> = {
  [CALENDAR_INVITES.Both]: '-176px',
  [CALENDAR_INVITES.Download]: '-122px',
  [CALENDAR_INVITES.subscribe]: '-86px',
};
export interface ButtonProp {
  label: string;
  name: string;
  disabled?: boolean;
  className?: string | null;
  eventId: string;
  eventName: string;
  iconButton?: boolean;
  downloadIcsFile: () => void;
  openGoogleCalendar: () => void;
  unSubscribeToCalendar?: () => void;
  isSubscribeToEvent?: boolean;
  withCalendarIcon?: boolean;
  customStyles?: React.CSSProperties;
  calendarInvite?: CALENDAR_INVITES;
  isSubscribed?: boolean;
  onShowDropDown?: (ref: RefObject<HTMLDivElement>) => void;
  withOptionsBoxStyle?: boolean;
  isSlider?: boolean;
}

export const OptionsButton: React.FunctionComponent<ButtonProp> = (props: ButtonProp): JSX.Element => {
  const {
    label,
    name,
    disabled,
    className = '',
    iconButton = false,
    downloadIcsFile,
    openGoogleCalendar,
    unSubscribeToCalendar,
    isSubscribeToEvent = false,
    calendarInvite,
    withCalendarIcon = false,
    customStyles = {},
    onShowDropDown,
    isSubscribed,
    withOptionsBoxStyle = false,
    isSlider = false,
  } = props;
  const ariaLabelForToggleButton = 'Add to Calendar options';
  const iconSize = iconButton ? '30px' : '25px';

  const mediaQuery = useMediaQuery() as MediaQueryContextType;
  const isMobile = mediaQuery?.isMobile;

  const optionsButtonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showDropDown, setShowDropDown] = useState(false);

  const handleOutsideClick = (e: MouseEvent) => {
    const target = e.target as Node;
    if (dropdownRef.current?.contains(target) || optionsButtonRef.current?.contains(target)) {
      return;
    }
    setShowDropDown(false);
  };

  const handleEscapeKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') setShowDropDown(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  useEffect(() => {
    if (!isSlider || !optionsButtonRef.current) return;

    const slide = optionsButtonRef.current.closest('.keen-slider__slide') as HTMLElement | null;
    if (!slide) return;

    const resetStyle = () => {
      slide.style.overflow = '';
      slide.style.zIndex = '';
      slide.style.position = '';
    };

    if (showDropDown) {
      slide.style.overflow = 'visible';
      slide.style.zIndex = '20';
      slide.style.position = 'relative';
    } else {
      resetStyle();
    }

    return () => resetStyle();
  }, [showDropDown, isSlider]);

  const toggleDropDown = () => {
    if (!showDropDown && onShowDropDown) onShowDropDown(optionsButtonRef);
    setShowDropDown(prev => !prev);
  };

  const closeDropDown = () => setShowDropDown(false);

  const getListItems = () => {
    const topOffset = iconButton && calendarInvite ? calendarInviteTopOffsets[calendarInvite] : undefined;

    return (
      <div className={`${withOptionsBoxStyle ? 'options-box' : ''} ${iconButton ? 'select-box' : ''}`}>
        <div
          ref={dropdownRef}
          style={{ ...customStyles, top: topOffset }}
          className={`select-box--items ${showDropDown ? 'block' : 'hidden'} ${iconButton ? 'select-box--right' : ''}`}
          onClick={closeDropDown}
        >
          {(calendarInvite === CALENDAR_INVITES.Both || calendarInvite === CALENDAR_INVITES.subscribe) &&
            <Button.Common
              name='unsubscribe'
              ariaLabel='unsubscribe'
              className='select-box--items--option bg-cool-gray-hover'
              onClick={() => {
                unSubscribeToCalendar?.();
                closeDropDown();
              }}
            >
              <Icon.RemoveFromCalendar width='29px' height='30px' viewBox='0 0 50 50' />
              <span className='select-box--option'>Remove from Calendar</span>
            </Button.Common>
          }
          {(!calendarInvite || calendarInvite === CALENDAR_INVITES.Both || calendarInvite === CALENDAR_INVITES.Download) &&
            <>
              <Button.Common
                name='addtoGoogleCalendar'
                ariaLabel='Add to Google Calendar'
                className='select-box--items--option bg-cool-gray-hover '
                onClick={() => {
                  openGoogleCalendar();
                  closeDropDown();
                }}
              >
                <Icon.GoogleCalendar width='30px' height='30px' viewBox='0 0 50 50' />
                <span className='select-box--option'>Add to Google Calendar</span>
              </Button.Common>
              <Button.Common
                className='bg-cool-gray-hover'
                name={`${props.eventName}.ics`}
                ariaLabel='Add to Outlook Calendar'
                onClick={() => {
                  downloadIcsFile();
                  closeDropDown();
                } }
              >
                <Icon.OutlookCalendar width='30px' height='30px' viewBox="0 0 50 50" />
                <span className='select-box--option'>{calendarInvite === CALENDAR_INVITES.Both ? 'Download Outlook file' : 'Add to Outlook Calendar (.ics)' }</span>
              </Button.Common>
            </>
          }
        </div>
      </div>
    );
  };

  return (
    <div
      ref={optionsButtonRef}
      className={`${!isMobile && !isSubscribeToEvent && !isSlider ? 'select-box--container' : ''}`}
    >
      {iconButton && !isSubscribed ?
        <Button.Common
          name={name}
          onClick={toggleDropDown}
          className='calendar-button transparent-bg without-after'
          ariaLabel={ariaLabelForToggleButton}
          aria-haspopup='true'
          aria-expanded={showDropDown ? 'true' : 'false'}
        >
          <div className='on-hover-shadow px-2 pb-2 rounded inline-block'>
            <Icon.AddToCalendar width='24px' height='25px' viewBox='0 0 50 50' className='fill-primary' />
          </div>
        </Button.Common>
        :
        <Button.Common
          ariaLabel={ariaLabelForToggleButton}
          name={name}
          onClick={toggleDropDown}
          label={!iconButton && label}
          disabled={disabled}
          className={`${iconButton ? 'pt-1 transparent-bg' : 'add-to-calendar-btn outline'} rounded text-center bg-white outline-mid-gray on-hover-shadow font-medium text-primary font-size-13px ${!iconButton && className ? className : ''} ${!iconButton && showDropDown ? 'select-box--button' : ''}`}
        >
          {isSubscribed && <Icon.CheckedCalendar width={iconSize} height={iconSize} viewBox='0 0 50 50' className={`col order-first mr-2`} />}
          {withCalendarIcon && !isSubscribed && <Icon.AddToCalendar width='20px' height='20px' viewBox='0 0 50 50' className='fill-primary fill-white col order-first mr-4' />}
        </Button.Common>
      }
      {getListItems()}
    </div>
  );
};

export default OptionsButton;
